<script>
    /*
    	ReviewMergePanel.svelte

        This panel allows cycling left services to be merged into a final Service.

        08/31 - no longer using MultiMergeForm

        The leftStore is still singular but can be populated from multiple Services API data.
        The rightStore in this MERGE case is initially null.

        NOTE - remember the structure of the JS ReviewObject:

            {
                .name
                .apiData
                .leftServiceObj
                .rightServiceObj
            }

        additionalInformation: []
        addressLine1: "101 HOME RD"
        availability: null
        city: "MOUNT GILEAD"
        costFreeText: null
        country: null
        county: "-"
        coverageAreas: []
        coverageCounties: null
        coverageGranularity: null
        coveragePostalCodes: null
        createdAt: "2022-08-02T22:32:59.796372Z"
        description: "-"
        eligibility: null
        exclusiveRequirements: null
        googleBusinessLink: null
        googleNumberRatings: null
        googleRating: null
        googleTestimonials: null
        id: 659245
        insuranceAccepted: []
        lastValidated: null
        lat: 40.5384
        locationPrecision: "street"
        lon: -82.8062
        mainEmail: "-"
        mainFacebook: null
        mainFax: null
        mainPhone: "4199464444"
        mainTwitter: null
        mainWebsite: "-"
        minerNotes: []
        necessaryDocuments: []
        necessaryMaterials: []
        nextSteps: null
        operatingHours: null
        otherSocialMedia: null
        postalCode: "43338"
        reviewed: 1
        serviceName: "MORROW COUNTY SHERIFF'S OFFICE AND CORRECTIONAL FACILITY"
        source: "-"
        state: "OH"
        supportedInsurance: null
        supportedLanguages: []
        taxonomies: null
        updatedAt: "2022-08-02T22:32:59.796372Z"

    */

    import { onMount } from 'svelte';
    import { ID } from '../../common/constants.js';

    // TCARE components
    import MultiMergePaginator from './MultiMergePaginator.svelte';
    import TaxonomyFieldsGroup from '../field-groups/TaxonomyFieldsGroup.svelte';
    import ServiceFieldsGroup1 from '../field-groups/ServiceFieldsGroup1.svelte';
    import ServiceFieldsGroup2 from '../field-groups/ServiceFieldsGroup2.svelte';
    import ServiceFieldsGroup3 from '../field-groups/ServiceFieldsGroup3.svelte';
    import ServiceFieldsGroup4 from '../field-groups/ServiceFieldsGroup4.svelte';
    import ServiceFieldsGroup6Stats from '../field-groups/ServiceFieldsGroup6Stats.svelte';
    import ServiceFieldsGroup5Coverage from '../field-groups/ServiceFieldsGroup5Coverage.svelte';

    // SMUI components
    import { Icon } from '@smui/icon-button';
	import { Svg } from '@smui/common/elements';
	import { mdiChevronRightBox, mdiRestore } from '@mdi/js';

    export let review;                              // non-store which contains 2 stores

    $: leftStore = review.leftServiceObj;           // Svelte store - empty until assigned
    $: leftStoreApiData = $leftStore.apiData;       // IMPORTANT - must use the apiData!
    $: rightStore = review.rightServiceObj;         // Svelte store - empty until assigned
    $: rightStoreApiData = $rightStore.apiData;     // IMPORTANT - must use the apiData!
    $: currentLeftIndex = -1;

    $: leftServicesApiData = review.apiData.leftServices || [];       // plural
    let rightServiceDataCache = null;

    onMount(() => {
        // console.log('');
        // console.log('ReviewMergePanel onMount: ');
        // console.log('review:');
        // console.log(review);

        // show the first leftService if there is one
        // also use this as the "default" finalService on the right
        if (leftServicesApiData.length > 0) {
            setCurrentIndex(0);         // this will also copy the api data into the leftStore
            $rightStore.copyApiDataFrom(leftServicesApiData[0]);
            $rightStore = $rightStore;              // refresh the UI for the field
        }

        rightServiceDataCache = JSON.parse(JSON.stringify(rightStoreApiData));           // cache
    })

    // called in the MultiMergePaginator - should range check here
    // IMPORTANT - must copy API data to leftService store!
    const setCurrentIndex = (idx) => {
        currentLeftIndex = idx;
        $leftStore.copyApiDataFrom(leftServicesApiData[currentLeftIndex]);
        $leftStore = $leftStore;              // refresh the UI for the field
    }

    // for DataTextFields
    const onCopyValue = (propName) => {
        if (propName === ID) return;
        if (!leftStoreApiData) return;
        rightStoreApiData[propName] = leftStoreApiData[propName];
        $rightStore = $rightStore;              // refresh the UI for the field
    }

    // for DataTaxonomyFields
    // copy using the JS Taxonomy object
    const onCopyTaxonomy = (taxName, taxObj) => {
        if (!$rightStore.taxonomies[taxName]) return;
        let targetTax = $rightStore.taxonomies[taxName];
        targetTax.copyToForestItems(taxObj);
        $rightStore = $rightStore;              // refresh the UI for the field
    }

    // from left side to right side
    // WHAT ABOUT PREVENTING id FROM BEING COPIED ???
    const copyAllValues = () => {
        $rightStore.copyApiDataFrom(leftStoreApiData);
        $rightStore = $rightStore;              // refresh the UI for the field
    }

    // right side resets itself - no copying from left
    const resetAllValues = () => {
        $rightStore.copyApiDataFrom(rightServiceDataCache);
        $rightStore = $rightStore;              // refresh the UI for the field
    }


</script>

<style>
    .review-merge-panel {
        width: 100%;
    }

    /* these CSS classes should go in a common area! */

    .flex-block {
        overflow: hidden;
    }

    .left-form {
        background-color: var(--tcare-left-bg);
        border-radius: 18px;
    }

    .right-form {
        background-color: var(--tcare-right-bg);        
        border-radius: 18px;
    }

    .copy-reset-all {
        font-size: 14px;
        padding: 10px 15px;
        color: white;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .copy-reset-all.left-header {
        background-color: var(--tcare-dull-purple);
    }

    .copy-reset-all.right-header {
        background-color: var(--tcare-purple-3);
    }

    :global(.copy-reset-all svg) {
        width: 24px;
        margin-left: 10px;
        font-size: 10px;
        fill: var(--tcare-white);
        outline: none;
    }

    :global(.copy-reset-all svg:hover) {
        fill: var(--tcare-secondary);
    }

    .review-type {
        font-size: 18px;
        font-weight: normal;
    }

    .padded-fields {
        padding: 20px;
    }

    .top-row {
        width: 98%;
        height: 50px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 15px;
        font-weight: 800;
        /* border: 1px solid purple; */
    }

    /*
    .remove-checkbox {
        font-size: 12px;
    }
    */
    
    .service-id {
        font-size: 15px;
        font-weight: 800;
        color: var(--tcare-primary);

    }

    :global(.review-merge-panel .fields-area .flex-block.w-49) {
        width: 100% !important;
        max-width: 100% !important;
    }
    

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<div class="review-merge-panel">
    <!-- only show if there are multiple services to merge -->
    {#if leftStoreApiData}

        <!-- row to hold the left/right blocks -->
        <div class="flex-row ai-start">

            <!-- LEFT SERVICES -->
            <div class="flex-block left-form w-49">
                <div class="copy-reset-all left-header" on:click={copyAllValues}>
                    <div class="flex-row review-type">Services to Merge</div>
                    <div class="flex-row jc-end w-50">
                        Copy all fields
                        <Icon component={Svg} viewBox="0 0 24 24" ><path d={mdiChevronRightBox} /></Icon>
                    </div>
                </div>

                <div class="padded-fields">
                    <div class="top-row">
                        <MultiMergePaginator services={leftServicesApiData} {setCurrentIndex} />
                        <!-- <div class="remove-checkbox">* Remove from merge</div> -->
                        <div class="service-id">{leftStoreApiData.id}</div>
                    </div>

                    <TaxonomyFieldsGroup
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyTaxonomy={onCopyTaxonomy}
                        isEditable={false}
                        isOpen={true}
                        showTcareTax1={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup1
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup2
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup3
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup4
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup5Coverage
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={true}
                        showDiffs={false}
                    />

                    <ServiceFieldsGroup6Stats
                        store={leftStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={onCopyValue}
                        isEditable={false}
                        isOpen={false}
                        showDiffs={false}
                    />

                </div>
            </div>    
            
            <!-- RIGHT SERVICE -->
            <div class="flex-block right-form w-49">
                <div class="copy-reset-all right-header" on:click={resetAllValues}>
                    <div class="flex-row review-type">Final Service</div>
                    <div class="flex-row jc-end w-50">
                        Reset all fields
                        <Icon component={Svg} viewBox="0 0 24 24" ><path d={mdiRestore} /></Icon>
                    </div>
                </div>

                <div class="padded-fields">
                    <div class="top-row" style={'width: 70%'}>
                        <div>{rightStoreApiData.serviceName}</div>
                    </div>

                    <TaxonomyFieldsGroup
                        store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyTaxonomy={null}
                        isEditable={true}
                        isOpen={true}
                        showTcareTax1={true}
                        showDiffs={false}
                    />

                    <ServiceFieldsGroup1
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={true}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup2
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={true}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup3
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={true}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup4
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={true}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup5Coverage
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={true}
                        isOpen={true}
                        showDiffs={false}
                    />
            
                    <ServiceFieldsGroup6Stats
                        bind:store={rightStore}
                        leftService={leftStoreApiData}
                        rightService={rightStoreApiData}
                        onCopyValue={null}
                        isEditable={false}
                        isOpen={false}
                        showDiffs={false}
                    />

                </div>
            </div>

        </div>
        
    {/if}

</div>
